import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getEmitterTypeToEnum } from '@semios/app-platform-values-helpers-and-types'
import type { TValuesCurrentIrrigationZones } from 'App/Map/types'
import type { GridTableContentSection } from 'components/GridTable/types'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { isUserOnlyAFreeRegionalUser } from 'utils/isUserOnlyAFreeRegionalUser'
import { getAllPointsForFlowMeter } from 'utils/setSelectedFieldAsset/_utils/getArgsForAnchorCoordinates'
import { apiArgsForIrrigationFlowMeter } from './_utils/apiArgsForIrrigationFlowMeter'
import { apiArgsForIrrigationZones } from './_utils/apiArgsForIrrigationZones'
import { flowMeterContent } from './flowMeter/flowMeter'
import { flowMeterTotals } from './flowMeterTotals/flowMeterTotals'
import {
  apiArgsForPressureSensors,
  pressureSensorsActivityContent,
} from './pressureSensorsActivity/pressureSensorsActivity'
import { irrigationZonesActivityContent } from './zones/irrigationActivityZones'

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  const pressureSensorArgs = apiArgsForPressureSensors({ selectedValueGroups, selectedFieldAssets })
  const irrigationZoneArgs = apiArgsForIrrigationZones({ selectedValueGroups, selectedFieldAssets })
  const irrigationFlowMeterArgs = apiArgsForIrrigationFlowMeter({ selectedValueGroups, selectedFieldAssets })

  const mergedPressureSensorArgsAndFlowMeterArgs = {
    points: {
      lngLats: [
        ...(pressureSensorArgs.points?.lngLats || []),
        ...(irrigationFlowMeterArgs.points?.lngLats || []),
      ],
      valuesRequested: {
        ...pressureSensorArgs.points?.valuesRequested,
        ...irrigationFlowMeterArgs.points?.valuesRequested,
      },
    },
  }

  return {
    ...mergedPressureSensorArgsAndFlowMeterArgs,
    ...irrigationZoneArgs,
  }
}

export const getIrrigationActivityCurrentValuesApiArgsByPropertyId = (propertyId: number) => {
  const property: TProperty | undefined = fieldAssetStore.getState().properties?.[propertyId]

  if (!property) return null

  const { irrigationZoneEmitters, points } = property
  const irrigationZoneIds: number[] = []
  const valueTypes: Partial<Record<TValuesCurrentIrrigationZones, boolean>> = {}

  Object.values(irrigationZoneEmitters || {}).forEach((irrigationZoneEmitter) => {
    irrigationZoneIds.push(+irrigationZoneEmitter.irrigationZoneId)

    const emitterType = getEmitterTypeToEnum(irrigationZoneEmitter.emitterType)
    const irrigationStatusKey = `irrigationIsOn_${emitterType}_status` as TValuesCurrentIrrigationZones

    valueTypes[irrigationStatusKey] = true
  })

  const returner: Partial<routes.ValuesCurrent.Request> = {}

  if (irrigationZoneIds.length)
    returner.irrigation_zones = {
      irrigationZoneIds: irrigationZoneIds,
      values: valueTypes,
    }

  if (points) {
    const pressureSensorPoints: string[] = []

    Object.entries(points).forEach(([lngLat, point]) => {
      if (point.valuesCurrent.includes('irrigationIsOn_status')) pressureSensorPoints.push(lngLat)
    })

    if (pressureSensorPoints.length)
      returner.points = {
        lngLats: pressureSensorPoints,
        values: valueTypes,
      }
  }

  return returner
}

export const currentValuesApiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.ValuesCurrent.Request> | null => {
  if (!selectedValueGroups.irrigation_activity_pressure || !selectedFieldAssets.property) return null

  return getIrrigationActivityCurrentValuesApiArgsByPropertyId(selectedFieldAssets.property)
}

export const content = ({
  data,
  selectedFieldAssets,
  currentValuesData,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
  currentValuesData: routes.ValuesCurrent.Response
}): GridTableContentSection[] => {
  const sectionsToShow: GridTableContentSection[] = [
    pressureSensorsActivityContent({ data, selectedFieldAssets, currentValuesData }),
    irrigationZonesActivityContent({ data, selectedFieldAssets, currentValuesData }),
  ]

  const isUserFreeRegional = isUserOnlyAFreeRegionalUser()

  const flowMeterHasPermission = checkAuthorization({
    permission: 'VIEW_IRRIGATION_FLOW_METER_DETAILS',
    entity: isUserFreeRegional ? 'FOR_ANY_ENTITY' : 'FOR_ANY_PROPERTY',
  })

  if (flowMeterHasPermission) {
    const { properties } = fieldAssetStore.getState()
    const property = properties?.[Number(selectedFieldAssets.property)]
    const flowMeterPointsForProperty = getAllPointsForFlowMeter(Object.values(property?.points ?? {}))

    sectionsToShow.push(
      flowMeterContent({ data, points: flowMeterPointsForProperty }),
      flowMeterTotals({ data, points: flowMeterPointsForProperty }),
    )
  }

  return sectionsToShow.filter((section) => Boolean(section))
}
